import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { toast } from "sonner";

const ShopContext = createContext(null);

export const money = (v) => `£${Number(v || 0).toFixed(2)}`;

export const qtyLabel = (i) => (i.sold_by_weight ? `${parseFloat(Number(i.qty).toFixed(2))} kg` : `${i.qty}`);

export const stockInfo = (product, threshold = 3) => {
    // Returns {state: 'untracked'|'out'|'low'|'ok', label}
    const stock = product?.stock;
    if (stock === null || stock === undefined) return { state: "untracked", label: "" };
    if (stock <= 0) return { state: "out", label: "Out of stock" };
    if (stock <= threshold) return { state: "low", label: `Only ${stock} left in stock` };
    return { state: "ok", label: "" };
};

export const isPerishable = (product, settings) =>
    !!settings?.perishable_enabled &&
    ((settings.perishable_categories || []).includes(product?.category) ||
        (settings.perishable_products || []).includes(product?.id));

export function ShopProvider({ children }) {
    const [basket, setBasket] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("freddies_basket") || "[]");
        } catch {
            return [];
        }
    });
    const [user, setUser] = useState(undefined); // undefined = checking, null = guest
    const [settings, setSettings] = useState({
        low_stock_threshold: 3,
        delivery_fee: 2.99,
        free_delivery_over: 30,
        delivery_enabled: true,
        collection_enabled: true,
        online_min_spend: 30,
        perishable_enabled: false,
        perishable_categories: [],
        perishable_products: [],
        perishable_hold_hours: 2,
    });

    useEffect(() => {
        localStorage.setItem("freddies_basket", JSON.stringify(basket));
    }, [basket]);

    useEffect(() => {
        api.get("/shop/settings").then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
        refreshUser();
    }, []);

    const refreshUser = () =>
        api
            .get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => setUser(null));

    const addToBasket = (product, variation = null, qty = 1) => {
        const price = variation ? variation.price : product.sale_price || product.price;
        setBasket((items) => {
            const key = (i) => `${i.product_id}|${i.variation || ""}`;
            const incoming = { product_id: product.id, variation: variation?.name || null };
            const existing = items.find((i) => key(i) === key(incoming));
            if (existing) {
                return items.map((i) => (key(i) === key(incoming) ? { ...i, qty: Math.min(i.qty + qty, 99) } : i));
            }
            return [
                ...items,
                {
                    product_id: product.id,
                    variation: variation?.name || null,
                    name: product.name,
                    price,
                    image: product.image,
                    unit: variation?.unit || product.unit || "each",
                    sold_by_weight: !!product.sold_by_weight,
                    qty,
                    allow_collection: product.allow_collection !== false,
                    allow_local_delivery: product.allow_local_delivery !== false,
                    allow_courier_delivery: product.allow_courier_delivery !== false,
                },
            ];
        });
        toast.success(`${product.name} added to basket`);
    };

    const updateQty = (product_id, variation, qty) => {
        const item = basket.find((i) => i.product_id === product_id && i.variation === variation);
        const byWeight = !!item?.sold_by_weight;
        const next = byWeight ? Math.round(qty * 4) / 4 : Math.round(qty);
        if (byWeight ? next < 0.25 : next < 1) return removeFromBasket(product_id, variation);
        setBasket((items) =>
            items.map((i) => (i.product_id === product_id && i.variation === variation ? { ...i, qty: Math.min(next, 99) } : i))
        );
    };

    const removeFromBasket = (product_id, variation) =>
        setBasket((items) => items.filter((i) => !(i.product_id === product_id && i.variation === variation)));

    const clearBasket = () => setBasket([]);

    const basketCount = basket.reduce((n, i) => n + (i.sold_by_weight ? 1 : i.qty), 0);
    const basketTotal = basket.reduce((n, i) => n + i.price * i.qty, 0);

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        setUser(null);
    };

    return (
        <ShopContext.Provider
            value={{
                basket, addToBasket, updateQty, removeFromBasket, clearBasket,
                basketCount, basketTotal, user, setUser, refreshUser, logout, settings,
            }}
        >
            {children}
        </ShopContext.Provider>
    );
}

export const useShop = () => useContext(ShopContext);
